import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { useCart, fmtMxn } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, CreditCard, AlertTriangle, Download } from "lucide-react";
import { track } from "@/lib/analytics";
import OrderBumpCard from "@/components/OrderBumpCard";

export default function Checkout() {
  const { cart, refresh, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [cohortsByProg, setCohortsByProg] = useState({});
  const [selections, setSelections] = useState({});
  const [contact, setContact] = useState({
    name: user?.full_name || "", email: user?.email || "",
    phone: "", company: "", tax_id: "", billing_address: "",
  });
  const [order, setOrder] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Fase 11 · MSI state
  const [installmentsMonths, setInstallmentsMonths] = useState(null);
  const [msiOptions, setMsiOptions] = useState([]);

  useEffect(() => {
    if (!cart?.total_mxn) return;
    api.get("/cart/msi-options").then((r) => setMsiOptions(r.data.options || [])).catch(() => {});
  }, [cart?.total_mxn]);

  useEffect(() => {
    if (!user) nav("/login?next=/checkout");
  }, [user, nav]);

  useEffect(() => {
    if (!cart?.items?.length) return;
    Promise.all(cart.items.map((it) =>
      api.get(`/catalog/programs/${it.program_snapshot.slug}`).then((r) => ({ pid: it.program_id, cohorts: r.data.upcoming_cohorts || [] }))
    )).then((results) => {
      const m = {};
      const init = {};
      results.forEach(({ pid, cohorts }) => {
        m[pid] = cohorts;
        if (cohorts.length > 0) init[pid] = cohorts[0].id;
      });
      setCohortsByProg(m);
      setSelections(init);
    });
  }, [cart]);

  useEffect(() => { track("checkout_started", { items: cart?.items?.length, total: cart?.total_mxn }); }, []);

  const startCheckout = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/start", {
        contact, cohort_selections: selections,
        installments_months: installmentsMonths || null,
      });
      setOrder(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const simulate = async (kind) => {
    if (!order) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/confirm-mock", {
        order_id: order.order_id, simulate_result: kind,
      });
      setResult({ ...data, order_number: order.order_number, total_mxn: order.total_mxn });
      if (data.status === "paid") {
        track("checkout_completed", { order_id: order.order_id, total_mxn: order.total_mxn, payment_provider: "mock" });
        await clear();
        await refresh();
        // Fase 13.4 · redirect a la página de upsell one-click.
        // Si no hay upsell disponible el propio UpsellOffer redirige a la orden.
        nav(`/checkout/upsell/${order.order_id}`, { replace: true });
        return;
      } else {
        track("checkout_failed", { reason: data.reason || "mock_failure" });
      }
      setStep(4);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  if (!cart) return <PublicLayout><p className="p-10 text-center text-gray-500">Cargando...</p></PublicLayout>;
  if (!cart.items?.length && step < 4) {
    return <PublicLayout><div className="max-w-xl mx-auto p-10 text-center">
      <p className="text-gray-600">Tu carrito está vacío.</p>
      <Link to="/catalogo" className="mt-3 inline-block text-[#F26A21]">Ver catálogo →</Link>
    </div></PublicLayout>;
  }

  const stripeConfigured = false; // Toggle to true when STRIPE_PUBLISHABLE_KEY exists

  const allCohortsSelected = cart.items.every((it) => selections[it.program_id]);
  const anyOutOfStock = cart.items.some((it) => (cohortsByProg[it.program_id] || []).length === 0);

  return (
    <PublicLayout>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Checkout</p>
        <h1 className="mt-2 text-3xl font-bold text-[#0B0B0B] font-display">Finaliza tu compra</h1>
        <Stepper step={step} />

        {step === 1 && (
          <div className="mt-8 space-y-4" data-testid="checkout-step-1">
            <p className="text-sm text-gray-600">Selecciona la cohorte para cada programa.</p>
            {anyOutOfStock && (
              <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-sm text-amber-800">
                Algunos programas ya no tienen cohortes disponibles. Contacta a un asesor.
              </div>
            )}
            {cart.items.map((it) => (
              <div key={it.program_id} className="p-4 bg-white border border-gray-200 rounded-lg"
                data-testid={`checkout-program-${it.program_snapshot.code}`}>
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                  {it.program_snapshot.format} · {it.program_snapshot.code}
                </p>
                <p className="mt-1 font-semibold text-[#0B0B0B]">{it.program_snapshot.title}</p>
                <div className="mt-3 space-y-2">
                  {(cohortsByProg[it.program_id] || []).map((c) => (
                    <label key={c.id} className={`flex items-center justify-between p-3 rounded border cursor-pointer ${
                      selections[it.program_id] === c.id ? "border-[#F26A21] bg-[#F26A21]/5" : "border-gray-200 hover:border-gray-300"
                    }`}>
                      <div className="flex items-center gap-2">
                        <input type="radio" name={`cohort-${it.program_id}`} value={c.id}
                          checked={selections[it.program_id] === c.id}
                          onChange={() => setSelections({ ...selections, [it.program_id]: c.id })}
                          data-testid={`checkout-cohort-${c.id}`}
                          className="accent-[#F26A21]" />
                        <div>
                          <p className="text-sm font-semibold text-[#0B0B0B]">{c.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{c.start_date} → {c.end_date}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 font-mono">{(c.enrolled_count || 0)}/{c.capacity || 0}</span>
                    </label>
                  ))}
                  {(cohortsByProg[it.program_id] || []).length === 0 && (
                    <p className="text-xs text-red-500">Sin cohortes disponibles.</p>
                  )}
                </div>
              </div>
            ))}
            <div className="flex justify-between">
              <Link to="/carrito" className="text-sm text-gray-600 hover:text-[#0B0B0B]">
                <ArrowLeft className="w-4 h-4 inline" /> Regresar al carrito
              </Link>
              <button onClick={() => setStep(2)} disabled={!allCohortsSelected}
                data-testid="checkout-step1-next"
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-40">
                Continuar <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); startCheckout(); }}
            className="mt-8 space-y-4" data-testid="checkout-step-2">
            <p className="text-sm text-gray-600">Datos de facturación</p>
            <div className="grid md:grid-cols-2 gap-3">
              <F label="Nombre completo *" val={contact.name} on={(v) => setContact({ ...contact, name: v })} testid="ck-name" required />
              <F label="Correo *" type="email" val={contact.email} on={(v) => setContact({ ...contact, email: v })} testid="ck-email" required />
              <F label="Teléfono" val={contact.phone} on={(v) => setContact({ ...contact, phone: v })} testid="ck-phone" />
              <F label="Empresa" val={contact.company} on={(v) => setContact({ ...contact, company: v })} testid="ck-company" />
              <F label="RFC (opcional)" val={contact.tax_id} on={(v) => setContact({ ...contact, tax_id: v.toUpperCase() })} testid="ck-rfc" />
              <F label="Dirección fiscal" val={contact.billing_address} on={(v) => setContact({ ...contact, billing_address: v })} testid="ck-address" />
            </div>

            {/* Fase 11 · MSI selection */}
            {msiOptions.length > 0 && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid="checkout-msi">
                <p className="text-xs font-bold uppercase tracking-widest text-[#F26A21]">Forma de pago</p>
                <div className="mt-2 space-y-2">
                  <label className={`block cursor-pointer rounded-lg border p-3 ${installmentsMonths === null ? "border-[#F26A21] bg-[#F26A21]/5" : "border-gray-200"}`}>
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={installmentsMonths === null}
                             onChange={() => setInstallmentsMonths(null)}
                             data-testid="ck-msi-single"
                             className="accent-[#F26A21]" />
                      <span className="text-sm font-semibold text-[#0B0B0B]">Pago único</span>
                    </div>
                  </label>
                  <label className={`block cursor-pointer rounded-lg border p-3 ${installmentsMonths !== null ? "border-[#F26A21] bg-[#F26A21]/5" : "border-gray-200"}`}>
                    <div className="flex items-center gap-2">
                      <input type="radio" checked={installmentsMonths !== null}
                             onChange={() => setInstallmentsMonths(msiOptions[0]?.months || 3)}
                             data-testid="ck-msi-msi"
                             className="accent-[#F26A21]" />
                      <span className="text-sm font-semibold text-[#0B0B0B]">Meses sin intereses</span>
                    </div>
                    {installmentsMonths !== null && (
                      <select value={installmentsMonths} onChange={(e) => setInstallmentsMonths(Number(e.target.value))}
                              data-testid="ck-msi-months"
                              className="mt-2 w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                        {msiOptions.map(o => (
                          <option key={o.months} value={o.months}>{o.label}</option>
                        ))}
                      </select>
                    )}
                  </label>
                </div>
                <p className="mt-2 text-[10px] text-gray-500">DCC absorbe la comisión de MSI · sin costo extra para ti.</p>
              </div>
            )}
            {/* Fase 13.4 · Order bump inline en checkout */}
            <OrderBumpCard onChanged={refresh} />

            <div className="flex justify-between">
              <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-600 hover:text-[#0B0B0B]">
                <ArrowLeft className="w-4 h-4 inline" /> Regresar
              </button>
              <button type="submit" disabled={submitting} data-testid="checkout-step2-next"
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-40">
                {submitting ? "..." : "Ir al pago"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        )}

        {step === 3 && order && (
          <div className="mt-8 space-y-6" data-testid="checkout-step-3">
            {!stripeConfigured && (
              <div className="p-4 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="mock-banner">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Modo simulación</p>
                  <p className="text-xs text-amber-800">El pago no procesará dinero real. Puedes simular éxito o fallo.</p>
                </div>
              </div>
            )}
            <div className="p-5 bg-[#F9FAFB] border border-gray-200 rounded-lg">
              <p className="text-xs uppercase tracking-widest text-gray-600 font-semibold">Orden {order.order_number}</p>
              <p className="mt-2 text-3xl font-bold text-primary font-mono" data-testid="checkout-total-display">{fmtMxn(order.total_mxn)}</p>
              <p className="text-xs text-gray-500 mt-1">Reserva de asientos vence en 15 minutos</p>

              {/* Fase 13.4 · desglose de línea (subtotal + bump + IVA) */}
              {cart && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-xs text-gray-700" data-testid="checkout-breakdown">
                  <div className="flex justify-between">
                    <span>Subtotal programas</span>
                    <span className="font-mono tabular-nums">{fmtMxn(cart.programs_subtotal_mxn || 0)}</span>
                  </div>
                  {(cart.bump_price_mxn || 0) > 0 && (
                    <div className="flex justify-between text-primary font-semibold" data-testid="checkout-breakdown-bump">
                      <span>+ Complemento ({cart.bump_snapshot?.title})</span>
                      <span className="font-mono tabular-nums">+{fmtMxn(cart.bump_price_mxn)}</span>
                    </div>
                  )}
                  {(cart.discount_mxn || 0) > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Descuento{cart.discount_code ? ` (${cart.discount_code})` : ""}</span>
                      <span className="font-mono tabular-nums">-{fmtMxn(cart.discount_mxn)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>IVA (16%)</span>
                    <span className="font-mono tabular-nums">{fmtMxn(cart.tax_mxn || 0)}</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-gray-200 mt-1 font-bold text-[#0B0B0B]">
                    <span>Total</span>
                    <span className="font-mono tabular-nums">{fmtMxn(cart.total_mxn || order.total_mxn)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 bg-white border border-gray-200 rounded-lg space-y-3">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-gray-400" aria-hidden="true" />
                <p className="text-sm font-semibold text-[#0B0B0B]">Datos de tarjeta (simulación)</p>
              </div>
              <input placeholder="4242 4242 4242 4242" disabled data-testid="mock-card-number"
                className="w-full bg-gray-100 border border-gray-200 rounded px-3 py-2 text-sm text-gray-400" />
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="MM/AA" disabled className="bg-gray-100 border border-gray-200 rounded px-3 py-2 text-sm text-gray-400" />
                <input placeholder="CVV" disabled className="bg-gray-100 border border-gray-200 rounded px-3 py-2 text-sm text-gray-400" />
                <input placeholder="Nombre" disabled className="bg-gray-100 border border-gray-200 rounded px-3 py-2 text-sm text-gray-400" />
              </div>
              <p className="text-[10px] text-gray-500">Los datos se ignoran en modo simulación.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <button onClick={() => simulate("success")} disabled={submitting}
                data-testid="mock-pay-success"
                className="py-3 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-40">
                {submitting ? "..." : "Simular pago exitoso"}
              </button>
              <button onClick={() => simulate("failure")} disabled={submitting}
                data-testid="mock-pay-failure"
                className="py-3 rounded-md bg-red-500 hover:bg-red-600 text-white font-semibold disabled:opacity-40">
                Simular pago fallido
              </button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="mt-8" data-testid="checkout-step-4">
            {result.status === "paid" ? (
              <div className="p-8 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl">
                <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Pago exitoso</p>
                <h2 className="mt-2 text-2xl font-bold text-[#0B0B0B] font-display">¡Gracias por tu compra!</h2>
                <p className="mt-2 text-sm text-gray-700">Orden {result.order_number} confirmada · {fmtMxn(result.total_mxn)}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/app/alumno/programas" data-testid="post-checkout-programs"
                    className="px-5 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm">
                    Ver mis programas
                  </Link>
                  <Link to={`/app/alumno/ordenes/${order.order_id}`} data-testid="post-checkout-order"
                    className="px-5 py-2 rounded-md border border-gray-300 text-[#0B0B0B] hover:bg-black/5 text-sm font-semibold inline-flex items-center gap-1">
                    <Download className="w-4 h-4" /> Ver factura
                  </Link>
                </div>
              </div>
            ) : (
              <div className="p-8 bg-red-500/10 border border-red-500/30 rounded-2xl" data-testid="checkout-failed-box">
                <p className="text-xs uppercase tracking-widest text-red-700 font-semibold">Pago fallido</p>
                <h2 className="mt-2 text-2xl font-bold text-[#0B0B0B] font-display">No pudimos procesar tu pago</h2>
                <p className="mt-2 text-sm text-gray-700">Los asientos se liberaron. Puedes reintentar o contactar a soporte.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/carrito" className="px-5 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm">Volver al carrito</Link>
                  <Link to="/contacto" className="px-5 py-2 rounded-md border border-gray-300 text-[#0B0B0B] hover:bg-black/5 text-sm font-semibold">Contactar soporte</Link>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}

function Stepper({ step }) {
  const steps = [
    { n: 1, l: "Cohortes" },
    { n: 2, l: "Facturación" },
    { n: 3, l: "Pago" },
    { n: 4, l: "Resultado" },
  ];
  return (
    <div className="mt-6 flex items-center gap-2 flex-wrap text-xs uppercase tracking-widest">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`flex items-center gap-2 ${step === s.n ? "text-[#F26A21]" : step > s.n ? "text-emerald-600" : "text-gray-400"}`}>
            <span className={`w-6 h-6 flex items-center justify-center rounded-full border font-mono ${
              step > s.n ? "bg-emerald-500/10 border-emerald-500/40" :
              step === s.n ? "bg-[#F26A21]/10 border-[#F26A21]/40" : "border-gray-300"
            }`}>{step > s.n ? <Check className="w-3 h-3" /> : s.n}</span>
            <span className="font-semibold">{s.l}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 min-w-4 ${step > s.n ? "bg-emerald-500/40" : "bg-gray-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function F({ label, val, on, testid, required, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
      <input value={val} onChange={(e) => on(e.target.value)} required={required} type={type}
        data-testid={testid}
        className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}
